import pluralize from 'pluralize'

import { DelimitedContent, HazardDetails, Icon } from '@fv/client-components'
import { formatNumber } from '@fv/client-core'
import type { Workflow } from '@fv/models'

import { useTypeNames } from '@/hooks/settings'
import type { ApiLoadHandlingUnitDTO } from '@/types/api-dto'

import { displayNmfc } from './commodityUtils'
import type { ItemSummaryInput } from './load-items/loadItemHelpers'

type Props = {
  item: ItemSummaryInput
  workflow: Workflow
  includeDescription?: boolean
  includeContains?: boolean
}

function itemHasContains(item: ItemSummaryInput): item is ItemSummaryInput & {
  contains: NonNullable<ApiLoadHandlingUnitDTO['contains']>
} {
  return 'contains' in item && !!item.contains?.length
}
// TODO - LoadLeg has this same logic with a duplicate type for the item.  We should change it over to LoadHandlingUnit
export const LoadItemSummary = (props: Props) => {
  const { item, includeDescription, includeContains, workflow } = props
  const { packageName } = useTypeNames()
  const {
    description,
    quantity,
    type,
    dimensionsUOM,
    length,
    width,
    height,
    weight,
    weightUOM,
    freightClass,
  } = item

  const hasDims = !!(height && length && width)
  return (
    <DelimitedContent delimiter={<>&nbsp;/&nbsp;</>}>
      {includeDescription && description && <span>{description}</span>}
      {pluralize(type ? packageName(type) : 'item', quantity, true)}
      {hasDims && (
        <span>
          {`Dimensions ${length} x ${width} x ${height} ${
            dimensionsUOM ?? 'in'
          }.`}
        </span>
      )}
      {item.nmfcNumber && workflow === 'ltl' && (
        <span>NMFC {displayNmfc(item)}</span>
      )}
      {freightClass && <span>Freight class {freightClass}</span>}
      {!!weight && (
        <span>
          Total weight {formatNumber(weight)} {weightUOM ?? 'lbs'}.
        </span>
      )}
      {includeContains && itemHasContains(item) && (
        <ContainsSummary {...props} />
      )}
      {item.hazardous?.length && <HazardSummary {...props} />}
    </DelimitedContent>
  )
}

const ContainsSummary = ({ item, includeContains }: Props) => {
  const { packageName } = useTypeNames()
  const contains = itemHasContains(item) ? item.contains : []
  if (!includeContains || !contains.length) return null

  const containStrings = contains.map(c =>
    pluralize(c.type ? packageName(c.type) : 'item', c.quantity, true),
  )
  return <span>Contains {containStrings.join(', ')}</span>
}
const HazardSummary = ({ item }: Props) => {
  const hazardous = item.hazardous?.[0]
  if (!hazardous) return null

  return (
    <>
      <Icon className="text-fv-orange" icon="radiation-alt" />
      <HazardDetails hazardDetails={[hazardous]} />
    </>
  )
}
